import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Pencil, Trash2, Wallet, Link2, TrendingDown, TrendingUp, Receipt, PiggyBank, Paperclip, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyExpense = {
  expense_date: new Date().toISOString().slice(0, 10),
  category: "Other",
  item_name: "",
  place: "",
  qty: 1,
  unit_price: "",
  description: "",
  vendor: "",
  amount: "",
  currency: "USD",
  payment_method: "cash",
  billable: false,
  linked_invoice_id: "",
  project_fund_id: "",
  receipt_url: "",
  notes: "",
};

function currentMonth() {
  return new Date().toISOString().slice(0, 7); // YYYY-MM
}

export default function Expenses() {
  const { t } = useI18n();
  const qc = useQueryClient();

  const [month, setMonth] = useState(currentMonth());
  const [filter, setFilter] = useState("all"); // all | billable | overhead
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExpense);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ["expenses", month],
    queryFn: () => api.listExpenses(month ? { month } : {}),
  });
  const { data: catData } = useQuery({
    queryKey: ["expenses-categories"],
    queryFn: () => api.expensesCategories(),
  });
  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices-for-expense-link"],
    queryFn: () => api.listInvoices(),
  });
  const { data: fundOptions = [] } = useQuery({
    queryKey: ["project-fund-options"],
    queryFn: () => api.projectFundOptions(),
  });

  const categories = catData?.categories || [
    "Rent", "Utilities", "Travel & transport", "Fuel", "Salaries", "Other",
  ];
  const paymentMethods = catData?.payment_methods || [
    "cash", "bank_transfer", "credit_card", "cheque", "other",
  ];

  const counts = useMemo(() => {
    const c = { all: expenses.length, billable: 0, overhead: 0 };
    for (const e of expenses) e.billable ? (c.billable += 1) : (c.overhead += 1);
    return c;
  }, [expenses]);

  const totals = useMemo(() => {
    const t = { all: 0, billable: 0, overhead: 0 };
    for (const e of expenses) {
      const a = Number(e.amount || 0);
      t.all += a;
      if (e.billable) t.billable += a; else t.overhead += a;
    }
    return t;
  }, [expenses]);

  const filtered = expenses.filter((e) => {
    if (filter === "all") return true;
    if (filter === "billable") return e.billable;
    return !e.billable;
  });

  const [receiptFile, setReceiptFile] = useState(null);
  const saveMut = useMutation({
    mutationFn: async (body) => {
      const saved = editing ? await api.updateExpense(editing.id, body) : await api.createExpense(body);
      if (receiptFile) {
        try { await api.uploadExpenseReceipt(saved.id, receiptFile); }
        catch (e) { toast.error(`Expense saved but the receipt failed: ${e?.response?.data?.detail || "upload error"}`); }
      }
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["project-funds"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyExpense);
      setReceiptFile(null);
      toast.success(editing ? "Expense updated" : "Expense recorded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteExpense(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["project-funds"] });
      setConfirmDeleteId(null);
      toast.success("Expense deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyExpense, expense_date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const removeReceipt = useMutation({
    mutationFn: (id) => api.deleteExpenseReceipt(id),
    onSuccess: (exp) => { qc.invalidateQueries({ queryKey: ["expenses"] }); setEditing(exp); toast.success("Receipt removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove receipt"),
  });
  const openEdit = (e) => {
    setReceiptFile(null);
    setEditing(e);
    setForm({
      expense_date: e.expense_date,
      category: e.category || "Other",
      item_name: e.item_name || "",
      place: e.place || "",
      qty: e.qty || 1,
      unit_price: e.unit_price || "",
      description: e.description || "",
      vendor: e.vendor || "",
      amount: e.amount,
      currency: e.currency || "USD",
      payment_method: e.payment_method || "cash",
      billable: !!e.billable,
      linked_invoice_id: e.linked_invoice_id || "",
      project_fund_id: e.project_fund_id || "",
      receipt_url: e.receipt_url || "",
      notes: e.notes || "",
    });
    setOpen(true);
  };

  // Auto-computed total when qty × unit_price both provided.
  const computedTotal = (Number(form.qty) || 0) * (Number(form.unit_price) || 0);
  const effectiveAmount = Number(form.amount) || computedTotal;

  const submit = () => {
    if (!form.description.trim() && !form.item_name.trim()) {
      toast.error("Item or description is required");
      return;
    }
    if (effectiveAmount <= 0) {
      toast.error("Total must be greater than 0 (enter amount, or qty × unit price)");
      return;
    }
    if (form.billable && !form.linked_invoice_id) {
      toast.error("Billable expenses need a linked invoice");
      return;
    }
    saveMut.mutate({
      ...form,
      qty: Number(form.qty) || 0,
      unit_price: Number(form.unit_price) || 0,
      amount: effectiveAmount,
      linked_invoice_id: form.linked_invoice_id || null,
      project_fund_id: form.project_fund_id || null,
    });
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader
        overline="finance"
        title="Expenses"
        actions={
          <div className="flex items-center gap-2">
            <Input
              data-testid="expense-month-filter"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-40"
            />
            <Button
              data-testid="expense-new-btn"
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> New expense
            </Button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <KPI
          testid="kpi-total"
          label="Total"
          icon={Wallet}
          amount={totals.all}
          count={counts.all}
          tone="slate"
        />
        <KPI
          testid="kpi-billable"
          label="Billable"
          icon={TrendingUp}
          amount={totals.billable}
          count={counts.billable}
          tone="emerald"
        />
        <KPI
          testid="kpi-overhead"
          label="Overhead"
          icon={TrendingDown}
          amount={totals.overhead}
          count={counts.overhead}
          tone="red"
        />
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="expense-tabs">
        {[
          { id: "all", label: "All", count: counts.all, tone: "slate" },
          { id: "billable", label: "Billable", count: counts.billable, tone: "emerald" },
          { id: "overhead", label: "Overhead", count: counts.overhead, tone: "red" },
        ].map((tab) => {
          const active = filter === tab.id;
          const toneMap = {
            slate: active ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50",
            emerald: active ? "bg-emerald-600 text-white ring-emerald-600" : "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50",
            red: active ? "bg-red-600 text-white ring-red-600" : "bg-white text-red-700 ring-red-200 hover:bg-red-50",
          };
          return (
            <button
              key={tab.id}
              data-testid={`expense-tab-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${toneMap[tab.tone]}`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="No expenses this month" hint="Click 'New expense' to record one" />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">No.</TableHead>
                <TableHead className="overline">Date</TableHead>
                <TableHead className="overline">Item</TableHead>
                <TableHead className="overline">Place</TableHead>
                <TableHead className="overline text-right">Qty</TableHead>
                <TableHead className="overline text-right">Unit price</TableHead>
                <TableHead className="overline">Category</TableHead>
                <TableHead className="overline">Linked to</TableHead>
                <TableHead className="overline text-right">Total</TableHead>
                <TableHead className="overline text-right w-24">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="mono text-[10px] text-muted-foreground">{e.expense_number || "—"}</TableCell>
                  <TableCell className="mono text-xs">{fmtDate(e.expense_date)}</TableCell>
                  <TableCell className="font-medium">
                    {e.item_name || e.description || "—"}
                    {e.item_name && e.description && (
                      <div className="text-[10px] text-muted-foreground">{e.description}</div>
                    )}
                    {e.vendor && <div className="text-[10px] text-muted-foreground">from {e.vendor}</div>}
                    {e.receipt && (
                      <button type="button" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline mt-0.5" data-testid={`expense-receipt-${e.id}`}
                              onClick={() => api.openExpenseReceipt(e.id).catch((err) => toast.error(err?.response?.data?.detail || "Receipt unavailable"))}>
                        <Paperclip className="size-3" /> {e.receipt.content_type?.startsWith("image/") ? "Receipt photo" : "Receipt PDF"}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.place || "—"}</TableCell>
                  <TableCell className="text-right mono text-xs">{e.qty ? Number(e.qty).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-right mono text-xs">{e.unit_price ? fmtMoney(e.unit_price) : "—"}</TableCell>
                  <TableCell className="text-xs">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200">
                      {e.category}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {e.linked_bill_name ? (
                      <span className="inline-flex items-center gap-1 text-slate-700 font-medium" data-testid={`expense-bill-tag-${e.id}`}>
                        <Receipt className="size-3" /> Bill · {e.linked_bill_name}
                      </span>
                    ) : e.project_fund_name ? (
                      <span className="inline-flex items-center gap-1 text-amber-800 font-medium" data-testid={`expense-fund-tag-${e.id}`}>
                        <PiggyBank className="size-3" />
                        {e.project_fund_name}
                      </span>
                    ) : e.linked_invoice_number ? (
                      <span className="inline-flex items-center gap-1 text-primary font-mono">
                        <Link2 className="size-3" />
                        {e.linked_invoice_number}
                        {e.linked_client_name && (
                          <span className="text-muted-foreground">· {e.linked_client_name}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Overhead</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right mono font-semibold">
                    {fmtMoney(e.amount)} {e.currency}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        data-testid={`expense-edit-${e.id}`}
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openEdit(e)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`expense-delete-${e.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        onClick={() => setConfirmDeleteId(e.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyExpense); setReceiptFile(null); } setOpen(v); }}>
        <DialogContent data-testid="expense-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit expense" : "Record a new expense"}</DialogTitle>
            <DialogDescription className="text-xs">
              Overhead expenses feed the monthly P&L. Billable expenses are pass-through costs attached to a client invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Date *</Label>
                <Input
                  data-testid="expense-form-date"
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Item</Label>
                <Input
                  data-testid="expense-form-item"
                  value={form.item_name}
                  onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                  placeholder="e.g. Return airfare · Fuel · Office paper"
                />
              </div>
              <div>
                <Label className="overline">Place</Label>
                <Input
                  data-testid="expense-form-place"
                  value={form.place}
                  onChange={(e) => setForm({ ...form, place: e.target.value })}
                  placeholder="e.g. Dili · Denpasar · Baucau"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="overline">Qty</Label>
                <Input
                  data-testid="expense-form-qty"
                  type="number" min="0" step="0.01" className="font-mono"
                  value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: e.target.value })}
                />
              </div>
              <div>
                <Label className="overline">Unit price</Label>
                <Input
                  data-testid="expense-form-unit-price"
                  type="number" min="0" step="0.01" className="font-mono"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="overline">Line total</Label>
                <div
                  data-testid="expense-form-line-total"
                  className="h-10 px-3 rounded-md border border-input bg-slate-50 font-mono text-sm flex items-center justify-end"
                >
                  {computedTotal.toFixed(2)}
                </div>
              </div>
            </div>
            <div>
              <Label className="overline">Description {form.item_name ? "" : "*"}</Label>
              <Input
                data-testid="expense-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional context — e.g. 'Bali client visit'"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Vendor / supplier</Label>
                <Input
                  data-testid="expense-form-vendor"
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="e.g. Garuda Indonesia"
                />
              </div>
              <div>
                <Label className="overline">Total (override)</Label>
                <div className="flex gap-1.5">
                  <Input
                    data-testid="expense-form-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    className="font-mono flex-1"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder={computedTotal ? computedTotal.toFixed(2) : "0.00"}
                  />
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="expense-form-currency" className="w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="IDR">IDR</SelectItem>
                      <SelectItem value="AUD">AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Payment method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="expense-form-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((p) => (
                      <SelectItem key={p} value={p}>{p.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Receipt URL (optional)</Label>
                <Input
                  data-testid="expense-form-receipt"
                  value={form.receipt_url}
                  onChange={(e) => setForm({ ...form, receipt_url: e.target.value })}
                  placeholder="https://…"
                />
              </div>
            </div>
            <div className="rounded-md border border-border p-3" data-testid="expense-receipt-box">
              <Label className="overline flex items-center gap-1"><Camera className="size-3" /> Receipt photo / PDF (optional)</Label>
              {editing?.receipt && !receiptFile ? (
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                  <button type="button" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="expense-form-receipt-view" onClick={() => api.openExpenseReceipt(editing.id)}><Paperclip className="size-3" /> {editing.receipt.name}</button>
                  <span className="text-muted-foreground">({Math.round((editing.receipt.size || 0) / 1024)} KB)</span>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-red-600" data-testid="expense-form-receipt-remove" disabled={removeReceipt.isPending} onClick={() => removeReceipt.mutate(editing.id)}>Remove</Button>
                  <label className="text-primary hover:underline cursor-pointer">Replace…<input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="expense-form-receipt-replace" onChange={(e) => { const x = e.target.files?.[0]; if (x && x.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; } setReceiptFile(x || null); }} /></label>
                </div>
              ) : (
                <Input data-testid="expense-form-receipt-file" type="file" accept="image/*,application/pdf" capture="environment" className="mt-1 text-xs"
                       onChange={(e) => { const x = e.target.files?.[0]; if (x && x.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); e.target.value = ""; return; } setReceiptFile(x || null); }} />
              )}
              <p className="text-[11px] text-muted-foreground mt-1">Take a photo on your phone or pick a file — stored securely with the expense{form.project_fund_id ? " and linked to the project fund cost" : ""}. Max 5 MB.{receiptFile ? ` Selected: ${receiptFile.name}` : ""}</p>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  data-testid="expense-form-billable"
                  type="checkbox"
                  className="mt-0.5 size-4 accent-emerald-600"
                  checked={!!form.billable}
                  onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                />
                <div className="text-xs leading-snug">
                  <div className="font-semibold text-emerald-900">Billable — pass through to a client invoice</div>
                  <div className="text-emerald-800/70">Check this if you'll bill this cost back to a client. You'll need to select the invoice below.</div>
                </div>
              </label>
              {form.billable && (
                <div className="mt-3">
                  <Label className="overline">Linked invoice *</Label>
                  <Select
                    value={form.linked_invoice_id}
                    onValueChange={(v) => setForm({ ...form, linked_invoice_id: v })}
                  >
                    <SelectTrigger data-testid="expense-form-invoice"><SelectValue placeholder="Select an invoice…" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {invoices.map((inv) => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.invoice_number} · {inv.client_snapshot?.name || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="expense-fund-box">
              <Label className="overline text-amber-900">Project fund (optional)</Label>
              <Select value={form.project_fund_id || "none"} onValueChange={(v) => setForm({ ...form, project_fund_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="expense-form-fund"><SelectValue placeholder="Not tied to a project fund" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">Not tied to a project fund</SelectItem>
                  {fundOptions.map((f) => (
                    <SelectItem key={f.id} value={f.id} data-testid={`expense-fund-option-${f.id}`}>
                      {f.name}{f.client_name ? ` · ${f.client_name}` : ""}{f.reference ? ` (${f.reference})` : ""}
                    </SelectItem>
                  ))}
                  {editing?.project_fund_id && !fundOptions.some((f) => f.id === editing.project_fund_id) && (
                    <SelectItem value={editing.project_fund_id}>{editing.project_fund_name || "Current fund"} (closed)</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-amber-800/80 mt-1.5">Tagged expenses are booked automatically as a project cost in that fund and are paid from its allocated money (not from cash on hand).</p>
            </div>
            <div>
              <Label className="overline">Notes (optional)</Label>
              <Textarea
                data-testid="expense-form-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="expense-form-cancel">Cancel</Button>
            <Button
              data-testid="expense-form-save"
              disabled={saveMut.isPending}
              className="bg-primary hover:bg-primary/90"
              onClick={submit}
            >
              {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Record expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(v) => !v && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expense-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="expense-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDeleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KPI({ testid, label, icon: Icon, amount, count, tone }) {
  const toneMap = {
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50",
    red: "border-red-200 bg-red-50",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    emerald: "text-emerald-700 bg-emerald-100",
    red: "text-red-700 bg-red-100",
  };
  return (
    <div data-testid={testid} className={`rounded-md border p-4 flex items-center justify-between ${toneMap[tone]}`}>
      <div>
        <p className="overline">{label}</p>
        <p className="text-2xl font-bold font-mono text-foreground mt-1">
          {fmtMoney(amount)}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{count} entr{count === 1 ? "y" : "ies"}</p>
      </div>
      <span className={`size-10 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-5" />
      </span>
    </div>
  );
}
